import React, { useMemo, useCallback, useState, useEffect } from 'react';
import isEqual from 'lodash.isequal';

import { routes } from 'config';
import { useRouter } from 'modules/shared/hooks';
import { alertService } from 'modules/shared-ui/components';
import { NumericCurrencyLabel } from 'modules/shared-ui-advanced/components';
import { SelectableVariant } from '../../../../../models';

import { ProductPriceDropdown } from '../product-price-dropdown';
import { ProductAddToListProps } from './product-add-to-list-type';

import styles from './product-add-to-list-component.module.css';

export const ProductAddToList: React.FC<ProductAddToListProps> = React.memo(
    ({ variants, currentElements, updateQuantities, isAuthenticated }: ProductAddToListProps) => {
        const [extendedVariants, setExtendedVariants] = useState<SelectableVariant[]>([]);
        const [previousState, setPreviousState] = useState(0);
        const { history, location } = useRouter();

        useEffect(() => {
            let result;
            if (variants.length > 0) {
                if (currentElements.length > 0) {
                    result = variants.map(v => {
                        const selectedVariant = currentElements.find(e => e.variantId === v.id);
                        return {
                            ...v,
                            quantity: selectedVariant ? selectedVariant.quantity : 0,
                        };
                    });
                } else {
                    result = variants.map(v => {
                        return {
                            ...v,
                            quantity: 0,
                        };
                    });
                }
            }

            setExtendedVariants(result || []);
        }, [variants, currentElements]);

        const localPrice = useMemo(
            () =>
                extendedVariants.length === 0
                    ? 0
                    : extendedVariants.map(v => v.quantity * v.price).reduce((a, v) => a + v),
            [extendedVariants],
        );

        const localQuantity = useMemo(
            () => (extendedVariants.length === 0 ? 0 : extendedVariants.map(v => v.quantity).reduce((a, v) => a + v)),
            [extendedVariants],
        );

        // 0: etat initial
        // 1: ajout
        // 2: ajouté
        // 3: modification
        // 4: modifié
        // 5: suppression
        const updateState = useMemo(() => {
            const remoteQuantity =
                currentElements.length === 0 ? 0 : currentElements.map(e => e.quantity).reduce((a, v) => a + v);

            let hasChanged = false;
            for (let i = 0; i < extendedVariants.length; i += 1) {
                const currentVariant = extendedVariants[i];
                const existingElement = currentElements.find(e => e.variantId === currentVariant.id);
                if (
                    (!existingElement && currentVariant.quantity > 0) ||
                    (existingElement && existingElement.quantity !== currentVariant.quantity)
                ) {
                    hasChanged = true;
                    break;
                }
            }

            if (hasChanged) {
                // 5: suppression
                if (localQuantity === 0) {
                    return 5;
                }
                // 3: modification
                if (remoteQuantity > 0) {
                    return 3;
                }

                // 1: ajout
                return 1;
            }

            // 2: ajouté
            if (remoteQuantity > 0) {
                if (previousState === 3) {
                    return 4;
                }

                return 2;
            }

            // 0: etat initial
            return 0;
        }, [extendedVariants, currentElements, localQuantity, previousState]);

        const manageUpdateVariantSelection = useCallback(
            (variantId: string, quantity: number) => {
                const currentSelectedIndex = extendedVariants.findIndex(v => v.id === variantId);
                extendedVariants[currentSelectedIndex].quantity = quantity;
                setExtendedVariants([...extendedVariants]);
            },
            [extendedVariants],
        );

        const manageUpdateQuantities = async (): Promise<void> => {
            if (!isAuthenticated) {
                history.push(`${routes.espacePerso.login}?redirectPath=${location.pathname}`);
            } else {
                let canUpdate = true;
                const checkedVariants: SelectableVariant[] = [];
                for (let i = 0; i < extendedVariants.length; i += 1) {
                    const currentVariant = extendedVariants[i];
                    const existingVariant = currentElements.find(e => e.variantId === currentVariant.id);
                    if (existingVariant && existingVariant.reservedQuantity > currentVariant.quantity) {
                        canUpdate = false;
                        checkedVariants.push({ ...currentVariant, quantity: existingVariant.reservedQuantity });
                    } else {
                        checkedVariants.push(currentVariant);
                    }
                }

                if (!canUpdate) {
                    await alertService.show(
                        'Impossible de supprimer cet article, un donateur a déjà fait un don ou réservé le produit',
                    );
                    setExtendedVariants(checkedVariants);
                } else {
                    const updatedVariants = extendedVariants.map(v => {
                        return { variantId: v.id, quantity: v.quantity };
                    });

                    setPreviousState(updateState);
                    updateQuantities(updatedVariants);
                }
            }
        };

        return (
            <React.Fragment>
                {variants && (
                    <div className={styles.quantityPicker}>
                        <div className={styles.quantity}>
                            <ProductPriceDropdown variants={extendedVariants} onChange={manageUpdateVariantSelection} />
                        </div>
                    </div>
                )}
                <div className={styles.priceSection}>
                    <div className={styles.addBtnContainer}>
                        <div className={styles.totalPrice}>
                            <NumericCurrencyLabel column label="Total" value={localPrice} />
                        </div>
                        <div className={styles.buttonRow}>
                            <button
                                type="button"
                                className={styles.invertedButton}
                                onClick={manageUpdateQuantities}
                                disabled={updateState === 0 || updateState === 2 || updateState === 4}
                            >
                                {(() => {
                                    switch (updateState) {
                                        case 2:
                                            return 'AJOUTÉ';
                                        case 3:
                                            return 'METTRE À JOUR MA LISTE';
                                        case 4:
                                            return 'LISTE MISE À JOUR';
                                        case 5:
                                            return 'SUPPRIMER DE MA LISTE';
                                        case 1:
                                        case 0:
                                        default:
                                            return 'AJOUTER À MA LISTE';
                                    }
                                })()}
                            </button>
                            {(updateState === 2 || updateState === 4) && (
                                <span className={styles.addedPicto}>
                                    Cet article a été ajouté à votre sélection. {localQuantity} pièces -{' '}
                                    {localPrice.toFixed(2)} €
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    },
    (prevProps, nextProps) => {
        return isEqual(prevProps, nextProps);
    },
);
